/**
 * Brush falloff curves.
 *
 * Mirrors the preset curves Blender ships on every sculpt brush
 * (`BKE_brush_curve_strength`): the brush's strength is scaled by a curve
 * evaluated on the *normalised* distance from the brush centre, so a stroke
 * blends into the surrounding surface instead of stamping a hard-edged disc.
 *
 * Every curve is a pure function of `t = distance / radius`:
 *   t = 0 (dead centre) -> 1   full strength
 *   t = 1 (brush rim)   -> 0   no effect
 *
 * Keeping them here — rather than inline in a brush kernel — means the same
 * curves drive the inflate brush, the relax pass, and any brush added later.
 */

export const BRUSH_FALLOFF = {
  /** Smoothstep. The Blender default: soft shoulders, no visible rim. */
  SMOOTH: 'smooth',
  /** Hemisphere. Wide plateau then a fast drop — good for volume changes. */
  SPHERE: 'sphere',
  /** Square root. Very wide, gentle — spreads the change over the whole disc. */
  ROOT: 'root',
  /** Quadratic. Concentrates the change near the centre. */
  SHARP: 'sharp',
  /** Straight line from centre to rim. */
  LINEAR: 'linear',
  /** Quartic. Tightest useful curve — nearly a point effect. */
  POW4: 'pow4',
  /** No falloff at all: a hard-edged disc. Rarely wanted on organic shapes. */
  CONSTANT: 'constant',
};

/**
 * The curve implementations. Each receives an already-clamped `t` in [0, 1].
 * @type {Record<string, (t: number) => number>}
 */
const CURVES = {
  [BRUSH_FALLOFF.SMOOTH]: (t) => {
    const s = 1 - t;
    return s * s * (3 - 2 * s);
  },
  [BRUSH_FALLOFF.SPHERE]: (t) => Math.sqrt(Math.max(0, 1 - t * t)),
  [BRUSH_FALLOFF.ROOT]: (t) => Math.sqrt(1 - t),
  [BRUSH_FALLOFF.SHARP]: (t) => (1 - t) * (1 - t),
  [BRUSH_FALLOFF.LINEAR]: (t) => 1 - t,
  [BRUSH_FALLOFF.POW4]: (t) => {
    const s = 1 - t;
    const s2 = s * s;
    return s2 * s2;
  },
  [BRUSH_FALLOFF.CONSTANT]: () => 1,
};

/** Human-readable labels, for populating a falloff picker in a tool card. */
export const BRUSH_FALLOFF_LABELS = {
  [BRUSH_FALLOFF.SMOOTH]: 'Smooth',
  [BRUSH_FALLOFF.SPHERE]: 'Sphere',
  [BRUSH_FALLOFF.ROOT]: 'Root',
  [BRUSH_FALLOFF.SHARP]: 'Sharp',
  [BRUSH_FALLOFF.LINEAR]: 'Linear',
  [BRUSH_FALLOFF.POW4]: 'Pow4',
  [BRUSH_FALLOFF.CONSTANT]: 'Constant',
};

export const DEFAULT_BRUSH_FALLOFF = BRUSH_FALLOFF.SMOOTH;

/**
 * Resolve a falloff id to its curve function, clamped at both ends.
 *
 * The returned function is safe to call in the innermost vertex loop: it does
 * no allocation and no property lookups, so resolve it once per stroke and
 * call it directly per vertex.
 *
 * @param {string} [name] - a {@link BRUSH_FALLOFF} value; unknown ids fall back to the default.
 * @returns {(t: number) => number} weight in [0, 1]
 */
export function getFalloffFn(name = DEFAULT_BRUSH_FALLOFF) {
  const curve = CURVES[name] ?? CURVES[DEFAULT_BRUSH_FALLOFF];
  return (t) => {
    if (t <= 0) return 1;
    if (t >= 1) return 0;
    return curve(t);
  };
}

/**
 * One-off falloff evaluation. Prefer {@link getFalloffFn} inside hot loops.
 * @param {number} t - normalised distance
 * @param {string} [name]
 * @returns {number}
 */
export function evaluateFalloff(t, name = DEFAULT_BRUSH_FALLOFF) {
  return getFalloffFn(name)(t);
}
